import React, {useEffect, useState} from "react";
import {Container, Wrapper} from "../../hoc/Layout";

// Те же хелперы, что и в Pay3
const isIOS = () =>
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent);

const isAndroid = () =>
    typeof navigator !== "undefined" &&
    /Android/i.test(navigator.userAgent);

const bankLabels = {
    tbank: "T-Bank",
    sber: "Сбербанк",
};

// Собираем диплинки по банку/платформе
function buildBankDeepLinks(bank, platform, card, amt) {
    if (!card) return {primaryUrl: "", allUrls: []};

    // T-Bank
    if (bank === "tbank") {
        const params =
            "amount=" +
            encodeURIComponent(amt) +
            "&targetCardNumber=" +
            encodeURIComponent(card);

        if (platform === "ios") {
            const url = "bank100000000004://Main/Pay/C2C?" + params;
            return {primaryUrl: url, allUrls: [url]};
        }

        const url = "tinkoffbank://Main/Pay/C2C?" + params;
        return {primaryUrl: url, allUrls: [url]};
    }

    // Сбербанк
    if (bank === "sber") {
        if (platform === "ios") {
            // iOS deeplinks для Сбера - расширенный список для максимальной совместимости
            const params =
                "amount=" +
                encodeURIComponent(amt) +
                "&isNeedToOpenNextScreen=true" +
                "&skipContactsScreen=true" +
                "&to=" +
                encodeURIComponent(card) +
                "&type=cardNumber";

            const paramsP2P =
                "type=card_number" +
                "&requisiteNumber=" +
                encodeURIComponent(card);

            const urls = [
                "sberbankonline://payments/p2ptransfer?" + params,
                "sberbankidlogin://payments/p2ptransfer?" + params,
                "sbolonline://payments/p2ptransfer?" + params,
                "onlineappmobile://sbolonline/p2ptransfer?" + params,
                "onlineios-app://sbolonline/p2ptransfer?" + params,
                "budgetonline-ios://sbolonline/p2ptransfer?" + params,
                "ios-app-smartonline://sbolonline/p2ptransfer?" + params,
                "sberbankonline://payments/p2p?" + paramsP2P,
                "sberbankidlogin://payments/p2p?" + paramsP2P,
                "sbolonline://payments/p2p?" + paramsP2P,
            ];

            return {primaryUrl: urls[0], allUrls: urls};
        }

        if (platform === "android") {
            // Android deeplinks для Сбера - расширенный список
            const params =
                "type=card_number" +
                "&requisiteNumber=" +
                encodeURIComponent(card);

            const urls = [
                "intent://ru.sberbankmobile/payments/p2p?" + params,
                "intent://ru.sberbankmobile/android-app/payments/p2p?" + params,
                "android-app://ru.sberbankmobile/payments/p2p?" + params,
                "android-app://ru.sberbankmobile_alpha/payments/p2p?" + params,
                "sberbankonline://payments/p2p?" + params,
            ];

            return {primaryUrl: urls[0], allUrls: urls};
        }

        const paramsP2P =
            "type=card_number" +
            "&requisiteNumber=" +
            encodeURIComponent(card);

        const url = "intent://ru.sberbankmobile/payments/p2p?" + paramsP2P;

        return {primaryUrl: url, allUrls: [url]};
    }

    return {primaryUrl: "", allUrls: []};
}

// Запуск нескольких диплинков подряд
function attemptOpenDeepLinks(urls) {
    if (!urls || !urls.length) return;

    let index = 0;

    const openNext = () => {
        const url = urls[index];
        try {
            window.location.href = url;
        } catch (e) {
            console.error("Failed to open deeplink", url, e);
        }
        index += 1;
        if (index < urls.length) {
            setTimeout(openNext, 1200);
        }
    };

    openNext();
}

function copyToClipboard(text) {
    if (!text) return;
    try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch((e) => {
                console.error("Clipboard write failed", e);
            });
        } else {
            const el = document.createElement("textarea");
            el.value = text;
            document.body.appendChild(el);
            el.select();
            try {
                document.execCommand("copy");
            } catch (err) {
                console.error("execCommand copy failed", err);
            }
            document.body.removeChild(el);
        }
    } catch (e) {
        console.error("Copy error", e);
    }
}

export default function PayTransfer() {
    const [data, setData] = useState({
        bank: "",
        card: "",
        amount: null,
        recipientName: "",
        error: "",
    });

    const mobile = isIOS() || isAndroid();

    // При заходе по ссылке — читаем параметры и пробуем открыть приложение
    useEffect(() => {
        if (typeof window === "undefined") return;

        const search = window.location.search || "";
        const qs = new URLSearchParams(search);

        const bank = qs.get("bank") || "";
        const card = qs.get("card") || "";
        const amountStr = qs.get("amount") || "";
        const name = qs.get("name") || "";

        if (!bank || !card || !amountStr) {
            setData((prev) => ({
                ...prev,
                error: "Некорректная ссылка для оплаты.",
            }));
            return;
        }

        const platform = isIOS()
            ? "ios"
            : isAndroid()
                ? "android"
                : "other";

        const {primaryUrl, allUrls} = buildBankDeepLinks(
            bank,
            platform,
            card,
            amountStr
        );

        if (!primaryUrl) {
            setData({
                bank,
                card,
                amount: Number(amountStr) || null,
                recipientName: name,
                error:
                    "Для этого банка пока нет автоматического открытия приложения. Используйте реквизиты ниже.",
            });
            return;
        }

        setData({
            bank,
            card,
            amount: Number(amountStr) || null,
            recipientName: name,
            error: "",
        });

        // На телефоне пробуем открыть приложение сразу
        if (platform === "ios" || platform === "android") {
            attemptOpenDeepLinks(allUrls);
        }
    }, []);

    const handleOpenAgain = () => {
        if (!data.bank || !data.card || !data.amount) return;

        const platform = isIOS()
            ? "ios"
            : isAndroid()
                ? "android"
                : "other";

        const {allUrls} = buildBankDeepLinks(
            data.bank,
            platform,
            data.card,
            String(data.amount)
        );

        if (allUrls && allUrls.length) {
            attemptOpenDeepLinks(allUrls);
        }
    };

    return (
        <Wrapper>
            <Container>
                <div
                    style={{
                        maxWidth: 520,
                        margin: "40px auto",
                        padding: "24px 20px",
                        borderRadius: 16,
                        background:
                            "linear-gradient(135deg, #f9fafb 0%, #eef2ff 80%)",
                        border: "1px solid #e5e7eb",
                        boxShadow:
                            "0 18px 30px rgba(15,23,42,0.08), 0 0 1px rgba(15,23,42,0.06)",
                        fontFamily:
                            '-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif',
                    }}
                >
                    <h1
                        style={{
                            fontSize: 20,
                            fontWeight: 700,
                            margin: "0 0 8px",
                            color: "#111827",
                        }}
                    >
                        Открытие платежа в приложении банка
                    </h1>

                    <p
                        style={{
                            fontSize: 14,
                            margin: "0 0 12px",
                            color: "#4b5563",
                        }}
                    >
                        {data.bank
                            ? `Банк: ${bankLabels[data.bank] || data.bank}`
                            : "Проверяем данные платежа…"}
                    </p>

                    {data.error && (
                        <div
                            style={{
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                color: "#b91c1c",
                                padding: "10px 12px",
                                borderRadius: 10,
                                fontSize: 13,
                                marginBottom: 12,
                            }}
                        >
                            {data.error}
                        </div>
                    )}

                    {mobile ? (
                        <p
                            style={{
                                fontSize: 14,
                                margin: "0 0 12px",
                                color: "#374151",
                            }}
                        >
                            Мы пытаемся открыть приложение банка с подготовленным
                            платежом. Если ничего не произошло в течение
                            нескольких секунд, нажмите кнопку ниже.
                        </p>
                    ) : (
                        <p
                            style={{
                                fontSize: 14,
                                margin: "0 0 12px",
                                color: "#374151",
                            }}
                        >
                            Откройте эту страницу на телефоне, где установлено
                            приложение выбранного банка. После этого мы
                            автоматически попробуем запустить приложение с
                            подготовленным платежом.
                        </p>
                    )}

                    {data.bank && (
                        <button
                            type="button"
                            onClick={handleOpenAgain}
                            style={{
                                width: "100%",
                                border: "none",
                                borderRadius: 9999,
                                padding: "10px 16px",
                                fontSize: 15,
                                fontWeight: 600,
                                background:
                                    "linear-gradient(135deg,#4f46e5,#6366f1)",
                                color: "#fff",
                                cursor: "pointer",
                                marginBottom: 16,
                            }}
                        >
                            Открыть платёж в{" "}
                            {bankLabels[data.bank] || data.bank}
                        </button>
                    )}

                    {/* Блок с реквизитами как запасной вариант */}
                    {(data.card || data.amount) && (
                        <div
                            style={{
                                marginTop: 8,
                                padding: "12px 14px",
                                background: "#ffffff",
                                borderRadius: 12,
                                border: "1px solid #e5e7eb",
                                fontSize: 14,
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 600,
                                    marginBottom: 10,
                                    color: "#111827",
                                }}
                            >
                                Реквизиты для ручного ввода
                            </div>

                            {data.card && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        padding: "8px 10px",
                                        borderRadius: 10,
                                        background: "#f9fafb",
                                        marginBottom: 6,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: "#111827",
                                                minWidth: 24,
                                            }}
                                        >
                                            №
                                        </div>
                                        <div
                                            style={{
                                                fontFamily: "monospace",
                                                wordBreak: "break-all",
                                                fontSize: 13,
                                            }}
                                        >
                                            {data.card}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => copyToClipboard(data.card)}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            padding: 4,
                                            cursor: "pointer",
                                            fontSize: 12,
                                            color: "#4b5563",
                                        }}
                                    >
                                        копировать
                                    </button>
                                </div>
                            )}

                            {data.recipientName && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        padding: "8px 10px",
                                        borderRadius: 10,
                                        background: "#f9fafb",
                                        marginBottom: 6,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: "#111827",
                                                minWidth: 32,
                                            }}
                                        >
                                            ФИО
                                        </div>
                                        <div
                                            style={{
                                                wordBreak: "break-word",
                                                fontSize: 13,
                                            }}
                                        >
                                            {data.recipientName}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            copyToClipboard(data.recipientName)
                                        }
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            padding: 4,
                                            cursor: "pointer",
                                            fontSize: 12,
                                            color: "#4b5563",
                                        }}
                                    >
                                        копировать
                                    </button>
                                </div>
                            )}

                            {data.amount && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        padding: "8px 10px",
                                        borderRadius: 10,
                                        background: "#f9fafb",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: "#111827",
                                                minWidth: 24,
                                            }}
                                        >
                                            ₽
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 15,
                                        }}
                                    >
                                        {data.amount.toLocaleString("ru-RU")} ₽
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Container>
        </Wrapper>
    );
}



// WEBPACK FOOTER //
// ./src/PagesOfCommon/PayTransfer/index.jsx